/**
 * Cart Mode Guard
 *
 * Prevents mixing gradwear and normal products in the cart.
 * - Gradwear products: have visible custom fields (Nickname, Full Student Name, etc.)
 * - Normal products: no visible custom fields
 *
 * Detection:
 * - Current product: data-is-gradwear attribute on <product-form-component>
 * - Cart items: line item properties checked via /cart.js
 *   Gradwear items have non-underscore-prefixed custom property keys.
 *   Normal items only have underscore-prefixed (hidden) custom property keys.
 */

const GRADWEAR_PROPERTY_KEYS = [
  'Preferred Name',
  'Nickname',
  'Full Student Name',
  'Grad Year Level',
  'Class/Group',
  'House',
  'First Name',
  'Last Name',
  'Jersey Number',
  'Additional Number',
];

/**
 * Check if a cart line item is a gradwear item by inspecting its properties.
 * Gradwear items have at least one visible (non-underscore-prefixed) custom field property.
 */
function isGradwearItem(item) {
  if (!item.properties) return false;
  return Object.keys(item.properties).some((key) => {
    return GRADWEAR_PROPERTY_KEYS.includes(key);
  });
}

/**
 * Fetch the current cart and determine its mode.
 * Always fetches fresh from /cart.js to avoid stale cache issues.
 * Returns: 'empty' | 'gradwear' | 'normal'
 */
async function getCartMode() {
  try {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    const cart = await response.json();

    console.log('[CartModeGuard] Cart items:', cart.items?.length, cart.items?.map(i => ({title: i.title, props: i.properties})));

    if (!cart.items || cart.items.length === 0) return 'empty';

    const hasGradwear = cart.items.some(isGradwearItem);

    console.log('[CartModeGuard] hasGradwear:', hasGradwear, '-> mode:', hasGradwear ? 'gradwear' : 'normal');

    if (hasGradwear) return 'gradwear';
    return 'normal';
  } catch (err) {
    console.error('[CartModeGuard] Failed to fetch cart:', err);
    return 'empty';
  }
}

/**
 * Show the cart mode conflict lightbox alert.
 */
function showCartModeAlert(cartMode, productType) {
  // Remove any existing alert
  const existing = document.getElementById('cart-mode-alert-overlay');
  if (existing) existing.remove();

  let message = '';
  if (cartMode === 'normal' && productType === 'gradwear') {
    message =
      'Your cart currently contains regular items. Please complete or clear your current order before adding personalised (gradwear) items.';
  } else if (cartMode === 'gradwear' && productType === 'normal') {
    message =
      'Your cart currently contains personalised (gradwear) items. Please complete or clear your current order before adding regular items.';
  }

  const overlay = document.createElement('div');
  overlay.id = 'cart-mode-alert-overlay';
  overlay.innerHTML = `
    <div class="cart-mode-alert__backdrop"></div>
    <div class="cart-mode-alert__dialog" role="alertdialog" aria-modal="true" aria-labelledby="cart-mode-alert-title">
      <div class="cart-mode-alert__icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#e53935" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
      </div>
      <h2 id="cart-mode-alert-title" class="cart-mode-alert__title">Items cannot be mixed</h2>
      <p class="cart-mode-alert__message">${message}</p>
      <button type="button" class="cart-mode-alert__close button">OK</button>
    </div>
  `;

  document.body.appendChild(overlay);

  const closeBtn = overlay.querySelector('.cart-mode-alert__close');
  const backdrop = overlay.querySelector('.cart-mode-alert__backdrop');

  function close() {
    overlay.remove();
  }

  closeBtn.addEventListener('click', close);
  backdrop.addEventListener('click', close);
  overlay.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });

  // Focus the close button for accessibility
  closeBtn.focus();
}

/**
 * Main guard check. Returns true if allowed, false if blocked.
 * If blocked, shows the lightbox alert.
 *
 * @param {boolean} isCurrentProductGradwear
 * @returns {Promise<boolean>}
 */
async function canAddToCart(isCurrentProductGradwear) {
  const cartMode = await getCartMode();

  const productType = isCurrentProductGradwear ? 'gradwear' : 'normal';
  console.log('[CartModeGuard] canAddToCart — cartMode:', cartMode, 'productType:', productType, 'isGradwear param:', isCurrentProductGradwear);

  // Empty cart — anything goes
  if (cartMode === 'empty') return true;

  // Same type — allowed
  if (cartMode === productType) return true;

  // Conflict — block and alert
  console.log('[CartModeGuard] BLOCKED — conflict detected');
  showCartModeAlert(cartMode, productType);
  return false;
}

/**
 * Show a success snackbar toast when item is added to cart.
 */
function showAddedToCartToast() {
  // Remove any existing toast
  const existing = document.getElementById('cart-success-toast');
  if (existing) existing.remove();

  // Hide the cart icon while toast is visible
  const cartAction = document.querySelector('.action__cart');
  if (cartAction) cartAction.classList.add('cart-action--hidden');

  const toast = document.createElement('div');
  toast.id = 'cart-success-toast';
  toast.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
    <span>Item added to cart</span>
  `;

  // Position toast near the cart icon
  if (cartAction) {
    const rect = cartAction.getBoundingClientRect();
    toast.style.top = rect.top + 'px';
    toast.style.right = (window.innerWidth - rect.right) + 'px';
  }

  document.body.appendChild(toast);

  // Trigger entrance animation
  requestAnimationFrame(() => toast.classList.add('cart-success-toast--visible'));

  // Auto-dismiss after 2.5 seconds, then reveal cart icon with updated count
  setTimeout(() => {
    toast.classList.remove('cart-success-toast--visible');
    toast.addEventListener('transitionend', () => {
      toast.remove();
      if (cartAction) cartAction.classList.remove('cart-action--hidden');
    }, { once: true });
    // Fallback removal
    setTimeout(() => {
      toast.remove();
      if (cartAction) cartAction.classList.remove('cart-action--hidden');
    }, 500);
  }, 2500);
}

// Expose globally so product-form.js can call it
window.CartModeGuard = { canAddToCart, getCartMode, showAddedToCartToast };

// Inject styles for the lightbox alert
(function injectStyles() {
  if (document.getElementById('cart-mode-guard-styles')) return;
  const style = document.createElement('style');
  style.id = 'cart-mode-guard-styles';
  style.textContent = `
    #cart-mode-alert-overlay {
      position: fixed;
      inset: 0;
      z-index: 99999;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .cart-mode-alert__backdrop {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
    }
    .cart-mode-alert__dialog {
      position: relative;
      background: #fff;
      border-radius: 8px;
      padding: 32px;
      max-width: 480px;
      width: 90%;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
      text-align: center;
      animation: cartModeAlertIn 0.2s ease-out;
    }
    @keyframes cartModeAlertIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
    .cart-mode-alert__icon {
      margin: 0 auto 16px;
      display: flex;
      justify-content: center;
    }
    .cart-mode-alert__title {
      margin: 0 0 12px;
      font-size: 1.25rem;
      font-weight: 600;
      color: #e53935;
    }
    .cart-mode-alert__message {
      margin: 0 0 24px;
      font-size: 1rem;
      line-height: 1.5;
      color: #333;
    }
    .cart-mode-alert__close {
      cursor: pointer;
      min-width: 120px;
      display: block;
      margin: 0 auto;
    }

    .cart-action--hidden {
      visibility: hidden !important;
    }

    /* Loading spinner for add-to-cart button */
    .atc-loading {
      position: relative;
    }
    .atc-loading .add-to-cart-text {
      visibility: hidden;
    }
    .atc-loading .add-to-cart-text--added {
      visibility: hidden;
    }
    .atc-loading::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      margin-top: -10px;
      margin-left: -10px;
      width: 20px;
      height: 20px;
      border: 2.5px solid rgba(255,255,255,0.3);
      border-top-color: #fff;
      border-radius: 50%;
      animation: atcSpin 0.6s linear infinite;
    }
    @keyframes atcSpin {
      to { transform: rotate(360deg); }
    }

    #cart-success-toast {
      position: fixed;
      top: 12px;
      right: 12px;
      transform: translateX(120%);
      opacity: 0;
      background: #e8f5e9;
      color: #2e7d32;
      border: 1px solid #a5d6a7;
      border-radius: 8px;
      padding: 10px 20px;
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.9rem;
      font-weight: 500;
      box-shadow: 0 2px 12px rgba(0,0,0,0.12);
      z-index: 99999;
      transition: opacity 0.35s ease, transform 0.35s ease;
      pointer-events: none;
      white-space: nowrap;
    }
    #cart-success-toast.cart-success-toast--visible {
      opacity: 1;
      transform: translateX(0);
    }
  `;
  document.head.appendChild(style);
})();

// Enable add-to-cart buttons that were disabled waiting for the guard to load
(function enableGuardedButtons() {
  document.querySelectorAll('button[data-guard-disabled]').forEach((btn) => {
    btn.removeAttribute('disabled');
    btn.removeAttribute('data-guard-disabled');
  });
})();
